import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Users, MessageCircle, Search, LogOut, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(userRes.data);
      
      const profileRes = await axios.get(`${API}/profiles/me`, { withCredentials: true });
      setProfile(profileRes.data);

      // Check subscription status
      const subRes = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(subRes.data);
      
      // Show modal if no active subscription
      if (!subRes.data.has_subscription) {
        setShowSubscriptionModal(true);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-gray-900">Healthcare Hearts</span>
          </div>
          
          <div className="flex items-center gap-4">
            {subscription?.has_subscription && (
              <Button
                variant="ghost"
                onClick={() => navigate('/pricing')}
                data-testid="manage-subscription-button"
                className="text-gray-600 hover:text-gray-900"
              >
                Manage Subscription
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome, {user?.name}!</h1>
          <p className="text-gray-600">
            {user?.verified ? (
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Verified {user.user_type === 'healthcare_provider' ? 'Healthcare Professional' : 'Community Member'}
              </span>
            ) : (
              <span className="text-amber-600">Profile verification pending</span>
            )}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <button
            onClick={() => navigate('/discovery')}
            data-testid="discovery-card"
            className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-200 group text-left"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Search className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Discover</h3>
            <p className="text-gray-600">Find and connect with healthcare professionals</p>
          </button>

          <button
            onClick={() => navigate('/matches')}
            data-testid="matches-card"
            className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-200 group text-left"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Matches</h3>
            <p className="text-gray-600">View your mutual connections</p>
          </button>

          <button
            onClick={() => navigate('/profile-setup')}
            data-testid="profile-card"
            className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-200 group text-left"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#06b6d4] to-[#0891b2] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <User className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Profile</h3>
            <p className="text-gray-600">{profile ? 'Edit your profile' : 'Complete your profile'}</p>
          </button>
        </div>

        {/* Subscription Modal */}
        {showSubscriptionModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" data-testid="subscription-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscribe to Continue</h2>
              <p className="text-gray-600 mb-6">
                Get full access to all features including browsing profiles, matching, and messaging for just $20/month.
              </p>
              <div className="space-y-3 mb-6">
                <Button
                  onClick={() => navigate('/pricing')}
                  data-testid="subscribe-now-button"
                  className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white py-3 rounded-lg font-semibold"
                >
                  Subscribe Now
                </Button>
                <Button
                  onClick={() => setShowSubscriptionModal(false)}
                  data-testid="close-modal-button"
                  variant="outline"
                  className="w-full border-gray-300 text-gray-700 py-3 rounded-lg"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Profile Completion */}
        {!profile && (
          <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Complete Your Profile</h2>
            <p className="text-gray-600 mb-6">
              Set up your profile to start discovering and matching with other healthcare professionals.
            </p>
            <Button
              onClick={() => navigate('/profile-setup')}
              data-testid="complete-profile-button"
              className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-6 py-3 rounded-lg font-medium"
            >
              Complete Profile
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
